import * as Sentry from "@sentry/react"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { RouterProvider } from "@tanstack/react-router"
import { useUnleashClient } from "@unleash/proxy-client-react"
import { lazy, Suspense, useEffect } from "react"

import { ErrorFallback } from "@/components/error-fallback"
import { OfflineBanner, PWAUpdatePrompt } from "@/components/pwa"
import { RouteLoading } from "@/components/route-loading"
import { ThemeProvider } from "@/components/theme-provider"
import { Toaster } from "@/components/ui/sonner"
import { TanStackRouterDevelopmentTools } from "@/lib/development-tools/TanStackRouterDevelopmentTools"
import { useAuth } from "@/store/auth"
import { useBreadcrumbs } from "@/store/breadcrumbs"
import { useOrganization } from "@/store/organization"
import type { TTanstackRouter } from "./main"

// Lazy load React Query DevTools - only in dev mode
const ReactQueryDevtools =
  process.env.NODE_ENV === "production"
    ? () => null
    : lazy(() =>
        import("@tanstack/react-query-devtools").then((module) => ({
          default: module.ReactQueryDevtools,
        })),
      )

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Re-fetch on mount only if stale
      refetchOnMount: true,
      // Enable structural sharing for performance
      structuralSharing: true,
      // Retry configuration
      retry: (failureCount, error) => {
        // Don't retry client errors (4xx)
        if (
          error instanceof Response &&
          error.status >= 400 &&
          error.status < 500
        ) {
          return false
        }
        return failureCount < 3
      },
      // Cache data for 10 seconds to avoid instant refetches on quick navigation
      staleTime: 10 * 1000,
      // Refetch on window focus
      refetchOnWindowFocus: true,
    },
    mutations: {
      retry: false,
    },
  },
})

type TAppProps = { router: TTanstackRouter }

const App = ({ router }: TAppProps) => {
  const client = useUnleashClient()
  const authentication = useAuth()
  const breadcrumbs = useBreadcrumbs()
  const organization = useOrganization()

  useEffect(() => {
    if (!client) return

    const user = authentication.user
    if (!user) {
      client.updateContext({ userId: undefined })

      return
    }

    client.updateContext({
      userId: user.id,
      properties: {
        userEmail: user.email,
      },
    })
  }, [client, authentication.user])

  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => <ErrorFallback resetError={resetError} />}
    >
      <ThemeProvider defaultTheme="system" storageKey="hwcompany-ui-theme">
        <OfflineBanner />
        <PWAUpdatePrompt />
        <QueryClientProvider client={queryClient}>
          <RouterProvider
            router={router}
            context={{ authentication, breadcrumbs, organization, queryClient }}
          />
          <TanStackRouterDevelopmentTools
            initialIsOpen={false}
            position="bottom-left"
            router={router}
          />
          <Suspense fallback={<RouteLoading />}>
            <ReactQueryDevtools initialIsOpen={false} position="bottom" />
          </Suspense>
        </QueryClientProvider>

        <Toaster />
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  )
}

export default App
